/**
 * Edit Group Store
 *
 * Manages the complete Edit Group Page state including group metadata, learning collection associations,
 * and product license associations.
 * Loads all data from a single unified endpoint using link-driven architecture.
 *
 * State machine: waiting → loading → ready → updating → success/error
 */
import { action, makeObservable, observable } from 'mobx';
import { succeed } from 'jsonous';
import { Task } from 'taskarian';
import { Maybe, nothing, just } from 'maybeasy';
import { when } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { GroupError } from '../GroupStore/Types';
import { groupEditDataDecoder, GroupEditData } from './Decoders';
import { callApi } from '../Appy';
import { findLinkT } from '../LinkyLinky';
import { currentUserStore } from '../CurrentUserStore';
import { Link, Resource } from '../Resource/Types';
import {
  State,
  Ready,
  UnsavedChanges,
  CancelingWithUnsavedChanges,
  EditLearningCollection,
} from './Types';
import {
  SelectableLicense,
  SelectedLicense,
  License,
  createSelectedLicense,
  createUnselectedLicense,
} from '../components/People/Participants/AddParticipant/Stores/Types';

// Type guard for SelectableLicense
const isSelectedLicense = (license: SelectableLicense): license is SelectedLicense =>
  license.kind === 'selected';

// Map an EditProductLicense to the shared `License` shape (drops the `selected` flag).
const toLicense = (pl: GroupEditData['productLicenses'][number]): License => ({
  id: pl.id,
  licenseName: pl.licenseName,
  licenseTypeName: pl.licenseTypeName,
  quantityAvailable: pl.quantityAvailable,
  startOn: pl.startOn,
  endOn: pl.endOn,
});

// Constants
export const MAX_NAME_LENGTH = 128;
export const MAX_DESCRIPTION_LENGTH = 10000;

// Curried helper to replace template parameters in link hrefs
const replaceInHref =
  (search: string, replacement: string) =>
  (link: Link): Link => ({
    ...link,
    href: link.href.replace(search, replacement),
  });

// Helper to convert EditProductLicense[] to SelectableLicense[]
const convertToSelectableLicenses = (
  productLicenses: GroupEditData['productLicenses'],
): SelectableLicense[] => {
  const selectedLicenses = productLicenses
    .filter((pl) => pl.selected)
    .map(
      (pl): License => ({
        id: pl.id,
        licenseName: pl.licenseName,
        licenseTypeName: pl.licenseTypeName,
        quantityAvailable: pl.quantityAvailable,
        startOn: pl.startOn,
        endOn: pl.endOn,
      }),
    )
    .map(createSelectedLicense);

  // If no licenses selected, return empty array (user can add with +)
  return selectedLicenses;
};

class EditGroupStore {
  @observable
  state: State = { kind: 'waiting' };

  // License-picker UI state for the shared GroupLicenseSelectionSection.
  @observable licenseSearchQuery = '';
  @observable expandedLicenseTypes: Set<string> = new Set();

  constructor(groupId: string) {
    makeObservable(this);
    // Immediately transition to loading and fetch data
    this.loadEditData(groupId);
  }

  // ------------------------------------------------------------------
  // Adapters that let the shared Add Group section components drive the
  // Edit Group form. The selection still lives in the existing
  // editResource / licenses fields, so the save-diff (getChanges /
  // getLicenseChanges) keeps working unchanged.
  // ------------------------------------------------------------------

  private get formState(): Maybe<Ready | UnsavedChanges | CancelingWithUnsavedChanges> {
    switch (this.state.kind) {
      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
        return just(this.state);
      case 'waiting':
      case 'loading':
      case 'updating':
      case 'success':
      case 'error':
        return nothing();
      default:
        return assertNever(this.state);
    }
  }

  get name(): string {
    return this.formState.map((s) => s.editResource.payload.name).getOrElseValue('');
  }

  get description(): string {
    return this.formState.map((s) => s.editResource.payload.description).getOrElseValue('');
  }

  get active(): boolean {
    return this.formState
      .map((s) => s.editResource.payload.status === 'active')
      .getOrElseValue(true);
  }

  get licenses(): ReadonlyArray<License> {
    return this.formState
      .map((s) => s.editResource.payload.productLicenses.map(toLicense))
      .getOrElseValue([]);
  }

  get selectedLicenseIds(): number[] {
    return this.formState
      .map((s) => s.licenses.filter(isSelectedLicense).map((l) => l.license.id))
      .getOrElseValue([]);
  }

  get collections(): ReadonlyArray<EditLearningCollection> {
    return this.formState.map((s) => s.editResource.payload.learningCollections).getOrElseValue([]);
  }

  get selectedCollectionIds(): number[] {
    return this.formState
      .map((s) =>
        s.editResource.payload.learningCollections.filter((c) => c.selected).map((c) => c.id),
      )
      .getOrElseValue([]);
  }

  /** A save/validation error to surface as a banner (Add Group shows one). */
  get formError(): Maybe<string> {
    // Surface whichever validation/save error is set (Add Group shows one banner).
    return this.formState.andThen((s) => (s.nameError.isJust() ? s.nameError : s.descriptionError));
  }

  get isSaving(): boolean {
    return this.state.kind === 'updating';
  }

  get canSubmit(): boolean {
    switch (this.state.kind) {
      case 'ready':
      case 'unsaved_changes':
        return (
          this.hasChanges &&
          this.name.trim().length > 0 &&
          !this.state.nameError.isJust() &&
          !this.state.descriptionError.isJust()
        );
      case 'waiting':
      case 'loading':
      case 'canceling_with_unsaved_changes':
      case 'updating':
      case 'success':
      case 'error':
        return false;
      default:
        return assertNever(this.state);
    }
  }

  @action setActive = (active: boolean) => {
    this.updateStatus(active ? 'active' : 'inactive');
  };

  @action setLicenseSearchQuery = (v: string) => {
    this.licenseSearchQuery = v;
  };

  @action toggleLicenseTypeExpanded = (typeName: string) => {
    const next = new Set(this.expandedLicenseTypes);
    if (next.has(typeName)) {
      next.delete(typeName);
    } else {
      next.add(typeName);
    }
    this.expandedLicenseTypes = next;
  };

  @action setSelectedLicenseIds = (ids: number[]) => {
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    const byId = new Map(
      this.state.editResource.payload.productLicenses.map((pl): [number, License] => [
        pl.id,
        toLicense(pl),
      ]),
    );
    const licenses = ids
      .map((id) => byId.get(id))
      .filter((l): l is License => l !== undefined)
      .map(createSelectedLicense);
    this.state = { ...this.state, kind: 'unsaved_changes', licenses };
  };

  @action setSelectedCollectionIds = (ids: number[]) => {
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    const idSet = new Set(ids);
    const learningCollections = this.state.editResource.payload.learningCollections.map((c) => ({
      ...c,
      selected: idSet.has(c.id),
    }));
    this.state = {
      ...this.state,
      kind: 'unsaved_changes',
      editResource: {
        ...this.state.editResource,
        payload: { ...this.state.editResource.payload, learningCollections },
      },
    };
  };

  @action
  loadEditData = (groupId: string) => {
    this.state = { kind: 'loading' };

    // Get 'edit-user-group' template link from currentUserStore singleton
    Task.succeed<GroupError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('edit-user-group'))
      .map(replaceInHref('{id}', groupId))
      .andThen(callApi(groupEditDataDecoder, {}))
      .fork(this.loadError, this.dataLoaded);
  };

  @action
  dataLoaded = (resource: Resource<GroupEditData>) => {
    if (this.state.kind === 'loading') {
      const licenses = convertToSelectableLicenses(resource.payload.productLicenses);

      this.state = {
        kind: 'ready',
        originalResource: resource,
        editResource: resource,
        nameError: nothing(),
        descriptionError: nothing(),
        licenses: licenses,
        originalLicenses: licenses,
      };
    }
  };

  @action
  loadError = (error: GroupError) => {
    if (this.state.kind === 'loading') {
      this.state = { kind: 'error', error };
    }
  };

  @action
  updateName = (name: string) => {
    // Only called when state is 'ready' or 'unsaved_changes' (enforced by UI)
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;

    // Prevent typing beyond max length
    const truncatedName = name.slice(0, MAX_NAME_LENGTH);

    // Show validation error only when input was truncated (exceeded max length)
    const nameError = when(
      name.length > MAX_NAME_LENGTH,
      `You've reached the maximum of ${MAX_NAME_LENGTH} characters for the group name.`,
    );

    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      editResource: {
        ...this.state.editResource,
        payload: {
          ...this.state.editResource.payload,
          name: truncatedName,
        },
      },
      nameError,
    };
  };

  @action
  updateDescription = (description: string) => {
    // Only called when state is 'ready' or 'unsaved_changes' (enforced by UI)
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;

    // Truncate if exceeds max length
    const truncatedDescription = description.slice(0, MAX_DESCRIPTION_LENGTH);

    // Set error only if the input was truncated (exceeded max length)
    const descriptionError = when(
      description.length > MAX_DESCRIPTION_LENGTH,
      `You've reached the maximum of ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters for the description.`,
    );

    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      editResource: {
        ...this.state.editResource,
        payload: {
          ...this.state.editResource.payload,
          description: truncatedDescription,
        },
      },
      descriptionError,
    };
  };

  @action
  updateStatus = (status: string) => {
    // Only called when state is 'ready' or 'unsaved_changes' (enforced by UI)
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      editResource: {
        ...this.state.editResource,
        payload: {
          ...this.state.editResource.payload,
          status,
        },
      },
    };
  };

  @action
  toggleLearningCollectionSelection = (id: number) => {
    // Only called when state is 'ready' or 'unsaved_changes' (UI disables during 'updating')
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;

    // Immutably update the editResource with the toggled collection
    const updatedCollections = this.state.editResource.payload.learningCollections.map((c) =>
      c.id === id ? { ...c, selected: !c.selected } : c,
    );

    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      editResource: {
        ...this.state.editResource,
        payload: {
          ...this.state.editResource.payload,
          learningCollections: updatedCollections,
        },
      },
    };
  };

  @action
  addLicense = (license: SelectableLicense) => {
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      licenses: [...this.state.licenses, license],
    };
  };

  @action
  removeLicenseAtIndex = (index: number) => {
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      licenses: this.state.licenses.filter((_, i) => i !== index),
    };
  };

  @action
  updateLicenseAtIndex = (index: number, license: SelectableLicense) => {
    if (this.state.kind !== 'ready' && this.state.kind !== 'unsaved_changes') return;
    const updatedLicenses = [...this.state.licenses];
    updatedLicenses[index] = license;
    this.state = {
      ...this.state,
      kind: 'unsaved_changes', // Always transition to unsaved_changes on edit
      licenses: updatedLicenses,
    };
  };

  getAvailableProductLicenses = (currentLicense: SelectableLicense): License[] => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return [];

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating': {
        const allLicenses = this.state.editResource.payload.productLicenses.map(
          (pl): License => ({
            id: pl.id,
            licenseName: pl.licenseName,
            licenseTypeName: pl.licenseTypeName,
            quantityAvailable: pl.quantityAvailable,
            startOn: pl.startOn,
            endOn: pl.endOn,
          }),
        );

        // Get IDs of already selected licenses (excluding current)
        const selectedIds = new Set(
          this.state.licenses
            .filter((l): l is SelectedLicense => isSelectedLicense(l) && l !== currentLicense)
            .map((l) => l.license.id),
        );

        return allLicenses.filter((license) => !selectedIds.has(license.id));
      }

      default:
        return assertNever(this.state);
    }
  };

  hasAvailableProductLicensesToAdd = (): boolean => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return false;

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating':
        return this.getAvailableProductLicenses(createUnselectedLicense()).length > 0;

      default:
        return assertNever(this.state);
    }
  };

  @action
  showUnsavedChangesModal = () => {
    // Transition from unsaved_changes to canceling_with_unsaved_changes to show modal
    if (this.state.kind === 'unsaved_changes') {
      this.state = { ...this.state, kind: 'canceling_with_unsaved_changes' };
    }
  };

  @action
  hideUnsavedChangesModal = () => {
    // Transition from canceling_with_unsaved_changes back to unsaved_changes to hide modal
    if (this.state.kind === 'canceling_with_unsaved_changes') {
      this.state = { ...this.state, kind: 'unsaved_changes' };
    }
  };

  @action
  submit = () => {
    // Allow submission from both ready and unsaved_changes states
    if (this.state.kind === 'ready' || this.state.kind === 'unsaved_changes') {
      const current = this.state;

      // Don't submit if there are validation errors
      if (current.nameError.isJust() || current.descriptionError.isJust()) {
        return; // Validation error already shown in UI
      }

      const { name, description, status } = current.editResource.payload;

      // Proceed with API call
      this.state = {
        ...current,
        kind: 'updating',
        nameError: nothing(),
        descriptionError: nothing(),
      };

      const { add, remove } = this.getChanges();
      const { add_licenses, remove_licenses } = this.getLicenseChanges();

      Task.succeed<GroupError, ReadonlyArray<Link>>(current.editResource.links)
        .andThen(findLinkT('update'))
        .andThen(
          callApi(succeed({}), {
            name,
            description,
            status,
            add,
            remove,
            add_licenses,
            remove_licenses,
          }),
        )
        .fork(this.submitError, this.submitSuccess);
    }
  };

  @action
  submitError = (error: GroupError) => {
    if (this.state.kind === 'updating') {
      // Preserve form state and show error inline - return to unsaved_changes since data is still dirty
      const errorMessage = this.getErrorMessage(error);
      this.state = {
        ...this.state,
        kind: 'unsaved_changes',
        nameError: just(errorMessage),
        descriptionError: nothing(), // Clear description error on submit error
      };
    }
  };

  /**
   * Helper to extract a user-friendly error message from GroupError
   */
  private getErrorMessage = (error: GroupError): string => {
    // Handle standard JS Error
    if (error instanceof Error) {
      return error.message;
    }

    // Handle discriminated union errors
    if ('kind' in error) {
      switch (error.kind) {
        case 'server-error':
          // ServerError has AlreadyTranslatedText message - convert to string
          return String(error.message);
        case 'error':
          // Error has TPlainTextKey message - convert to string
          return String(error.message);
        case 'missing-link-error':
          return `Missing link: ${error.rel}`;
        case 'timeout':
          return 'The request timed out. Please try again.';
        case 'network-error':
          return 'Network error occurred. Please check your connection.';
        case 'bad-status':
          return 'Server returned an error. Please try again.';
        default:
          return 'An error occurred while saving. Please try again.';
      }
    }

    return 'An error occurred while saving. Please try again.';
  };

  @action
  submitSuccess = () => {
    if (this.state.kind === 'updating') {
      this.state = {
        kind: 'success',
        id: this.state.editResource.payload.id,
        name: this.state.editResource.payload.name,
        licenseCount: this.state.licenses.filter(isSelectedLicense).length,
        collectionCount: this.state.editResource.payload.learningCollections.filter(
          (c) => c.selected,
        ).length,
      };
    }
  };

  getSelectedIds(): number[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return [];

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating':
        return this.state.editResource.payload.learningCollections
          .filter((c) => c.selected)
          .map((c) => c.id);

      default:
        return assertNever(this.state);
    }
  }

  getChanges(): { add: number[]; remove: number[] } {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return { add: [], remove: [] };

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating': {
        const state = this.state;

        // Get original selected IDs from the immutable originalResource
        const originalSelectedIds = new Set(
          state.originalResource.payload.learningCollections
            .filter((c) => c.selected)
            .map((c) => c.id),
        );

        // Get current selected IDs from the current editResource
        const currentIds = new Set(
          state.editResource.payload.learningCollections.filter((c) => c.selected).map((c) => c.id),
        );

        const add = Array.from(currentIds).filter((id) => !originalSelectedIds.has(id));
        const remove = Array.from(originalSelectedIds).filter((id) => !currentIds.has(id));

        return { add, remove };
      }

      default:
        return assertNever(this.state);
    }
  }

  getLicenseChanges(): { add_licenses: number[]; remove_licenses: number[] } {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return { add_licenses: [], remove_licenses: [] };

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating': {
        const state = this.state;

        // Get original selected license IDs
        const originalLicenseIds = new Set(
          state.originalLicenses.filter(isSelectedLicense).map((l) => l.license.id),
        );

        // Get current selected license IDs
        const currentLicenseIds = new Set(
          state.licenses.filter(isSelectedLicense).map((l) => l.license.id),
        );

        const add_licenses = Array.from(currentLicenseIds).filter(
          (id) => !originalLicenseIds.has(id),
        );
        const remove_licenses = Array.from(originalLicenseIds).filter(
          (id) => !currentLicenseIds.has(id),
        );

        return { add_licenses, remove_licenses };
      }

      default:
        return assertNever(this.state);
    }
  }

  get hasChanges(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return false;

      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating': {
        // Check metadata changes against original resource
        const metadataChanged =
          this.state.editResource.payload.name !== this.state.originalResource.payload.name ||
          this.state.editResource.payload.description !==
            this.state.originalResource.payload.description ||
          this.state.editResource.payload.status !== this.state.originalResource.payload.status;

        // Check collection changes
        const { add, remove } = this.getChanges();
        const collectionsChanged = add.length > 0 || remove.length > 0;

        // Check license changes
        const { add_licenses, remove_licenses } = this.getLicenseChanges();
        const licensesChanged = add_licenses.length > 0 || remove_licenses.length > 0;

        return metadataChanged || collectionsChanged || licensesChanged;
      }

      default:
        return assertNever(this.state);
    }
  }
}

export default EditGroupStore;
